import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const GOLD = '#d4af37';

interface RulesContent {
  title: string;
  description: string;
  dressCode: string;
  conductRules: string;
  worshipGuidelines: string;
  photographyPolicy: string;
  specialObservances: string;
  safetyMeasures: string;
}

const RulesAboutManager = () => {
  const [content, setContent] = useState<RulesContent>({
    title: 'Rules & Guidelines',
    description: 'Important rules and guidelines for visitors',
    dressCode: 'Devotees should wear modest and clean clothing when visiting the temple',
    conductRules: 'Maintain silence and reverence within the temple premises',
    worshipGuidelines: 'Guidelines for proper worship and ritual participation',
    photographyPolicy: 'Photography and videography regulations within the temple',
    specialObservances: 'Special observances and restrictions during festivals',
    safetyMeasures: 'Safety guidelines and emergency procedures'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'about_content', 'rules'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as RulesContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'about_content', 'rules'), content);
      showSnackbar('Rules content saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field: keyof RulesContent, value: string) => {
    setContent({ ...content, [field]: value });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Rules & Guidelines Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage temple rules and visitor guidelines
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📋 Rules & Guidelines Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Title"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={content.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Dress Code"
              value={content.dressCode}
              onChange={(e) => handleChange('dressCode', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Conduct Rules"
              value={content.conductRules}
              onChange={(e) => handleChange('conductRules', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Worship Guidelines"
              value={content.worshipGuidelines}
              onChange={(e) => handleChange('worshipGuidelines', e.target.value)}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Photography Policy"
              value={content.photographyPolicy}
              onChange={(e) => handleChange('photographyPolicy', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Special Observances"
              value={content.specialObservances}
              onChange={(e) => handleChange('specialObservances', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Safety Measures"
              value={content.safetyMeasures}
              onChange={(e) => handleChange('safetyMeasures', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RulesAboutManager;
